import { useEffect, useState } from 'react';
import { getTenantFromDomain } from '@/utils';
import useFullUrl from './useFullUrl';

export default function useUserpilot(user, pathname) {
  const [userpilotReady, setUserpilotReady] = useState(false);
  const fullUrl = useFullUrl();
  let lastUrl = typeof window !== 'undefined' ? window.location.href : '';

  // LOAD USERPILOT SDK (ONCE)
  useEffect(() => {
    const isCypress = window.Cypress;
    if (isCypress) return;
    if (typeof window === 'undefined') return;

    try {
      const hostname = window.location.hostname.toLowerCase();
      const tenant = getTenantFromDomain();
      // allow ONLY fmg tenant
      const isFmgTenant = tenant === 'fmg';
      // tenant-based token
      const USERPILOT_TOKEN = isFmgTenant
        ? process.env.REACT_APP_USERPILOT_TOKEN_FMG
        : process.env.REACT_APP_USERPILOT_TOKEN_CFC;

      if (!USERPILOT_TOKEN) {
        console.error('[Userpilot] token missing');
        return;
      }

      console.table([
        { UserpilotToken: USERPILOT_TOKEN, Domain: hostname, Tenant: tenant },
      ]);

      // Settings
      if (!document.getElementById('userpilot-settings')) {
        const settings = document.createElement('script');
        settings.id = 'userpilot-settings';
        settings.innerHTML = `
          window.userpilotSettings = { token: "${USERPILOT_TOKEN}" };
        `;
        document.head.appendChild(settings);
      }

      // SDK
      if (!document.getElementById('userpilot-sdk')) {
        const sdk = document.createElement('script');
        sdk.id = 'userpilot-sdk';
        sdk.async = true;
        sdk.src = 'https://js.userpilot.io/sdk/latest.js';
        sdk.onload = () => {
          console.log('[Userpilot] SDK ready');
          setUserpilotReady(true);
        };
        document.head.appendChild(sdk);
      } else {
        setUserpilotReady(true);
      }
    } catch (err) {
      console.warn('[Userpilot] bootstrap failed:', err);
    }
  }, []);

  // IDENTIFY USER (DUPLICATES ALLOWED)
  useEffect(() => {
    const isCypress = window.Cypress;
    if (isCypress) return;
    if (!userpilotReady) return;

    const userpilot = window.userpilot;
    if (!userpilot) return;
    if (!user?.id || !user?.companyId) return;

    const formatCompanyName = (name) => {
    if (!name) return name;

    const normalized = name.toLowerCase().replace(/\s+/g, '');

    if (normalized === 'agilechain') return 'AgileChain';
    if (normalized === 'cfco') return 'CFCo';
    if (normalized === 'fultonmarketgroup') return 'Fulton Market Group';

    return name;
  };

    // kebab-case helper, e.g. "Fulton Market Group" -> "fulton-market-group"
    const toKebabCase = (value) => {
      if (!value) return '';
      return String(value).trim().toLowerCase().replace(/\s+/g, '-');
    };

    // Userpilot company id: kebab-case of the company name, falling back to the raw company id
    const getUserpilotCompanyId = (companyName, companyId) => {
      if (companyName) return toKebabCase(companyName);
      return companyId ? String(companyId) : '';
    };

    // Userpilot user id (username): kebab-case of "first last", falling back to the raw user id
    const getUserpilotUserId = (u) => {
      const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
      if (fullName) return toKebabCase(fullName);
      return u.id ? String(u.id) : '';
    };

    const formattedCompanyName = formatCompanyName(user.companyName);
    const userpilotUserId = getUserpilotUserId(user) || user.id;
    const userpilotCompanyId = getUserpilotCompanyId(formattedCompanyName || user.companyName, user.companyId);

    userpilot.identify(userpilotUserId, {
      name: user.name,
      email: user.email,
      company: {
        id: userpilotCompanyId,
        name: formattedCompanyName,
      },
      role: user.role?.name,
    });
  }, [userpilotReady, user]);

  // SPA FULL URL CHANGES (PATH, QUERY PARAMS, FRAGMENTS)
  useEffect(() => {
    const isCypress = window.Cypress;
    if (isCypress) return;
    if (!userpilotReady) return;
    if (!fullUrl) return;
    
    console.log('[Userpilot] Full URL changed, reloading', { fullUrl });

    const userpilot = window.userpilot;
    if (userpilot?.reload) {
      userpilot.reload();
    }
  }, [fullUrl, userpilotReady]);

  // DIRECT URL CHANGE LISTENER FOR USERPILOT (Query params & hash changes)
  useEffect(() => {
    const isCypress = window.Cypress;
    if (isCypress) return;
    if (!userpilotReady) return;
    if (typeof window === 'undefined') return;

    let lastUrlTracked = window.location.href;

    const handleUrlChange = () => {
      const currentUrl = window.location.href;
      if (currentUrl !== lastUrlTracked) {
        lastUrlTracked = currentUrl;
        const userpilot = window.userpilot;
        if (userpilot && typeof userpilot.reload === 'function') {
          console.log('[Userpilot] URL changed (query/hash), reloading', {
            url: currentUrl
          });
          userpilot.reload();
        }
      }
    };

    // Listen for hash changes
    window.addEventListener('hashchange', handleUrlChange);

    // Listen for popstate (back/forward button)
    window.addEventListener('popstate', handleUrlChange);

    // Poll for URL changes (for client-side navigation that doesn't trigger events)
    const interval = setInterval(() => {
      const currentUrl = window.location.href;
      if (currentUrl !== lastUrlTracked) {
        lastUrlTracked = currentUrl;
        const userpilot = window.userpilot;
        if (userpilot && typeof userpilot.reload === 'function') {
          console.log('[Userpilot] URL changed (detected via polling), reloading', {
            url: currentUrl
          });
          userpilot.reload();
        }
      }
    }, 500); // Check every 500ms

    return () => {
      window.removeEventListener('hashchange', handleUrlChange);
      window.removeEventListener('popstate', handleUrlChange);
      clearInterval(interval);
    };
  }, [userpilotReady]);

  // TRACK WINDOW CLOSE EVENT IN USERPILOT (Only when window/tab is actually closed)
  useEffect(() => {
    const isCypress = window.Cypress;
    if (isCypress) return;
    if (typeof window === 'undefined') return;

    let isPageRefreshOrNavigation = false;

    // Detect page refresh or navigation
    const handleBeforeUnload = (event) => {
      // Check if it's a page refresh (F5, Ctrl+R, etc.)
      // or navigation (clicking links, form submission, etc.)
      // In both cases, we set the flag to true
      isPageRefreshOrNavigation = true;
    };

    // Detect actual window/tab close
    const handleUnload = () => {
      // Only track if it's NOT a page refresh or navigation
      // This means the user actually closed the window/tab
      if (!isPageRefreshOrNavigation) {
        const userpilot = window.userpilot;
        if (userpilot && typeof userpilot.track === 'function') {
          console.log('[Userpilot] Tracking event: window closed');
          userpilot.track('window closed');
        }
      }
    };

    // Listen for beforeunload to detect page refresh or navigation
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Listen for unload to detect actual window/tab close
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, []);

  console.log({
    ready: userpilotReady,
    uid: user?.id,
    email: user?.email,
    fullUrl: fullUrl,
  });
}
